/**
 * AdminWhatsAppConexion.jsx · v101.180.0 · Conexión con WhatsApp Business (Meta Cloud API).
 * Estado de la configuración (sin mostrar secretos), datos del número según Meta, asociación de cada mensaje de
 * AlfaField con su plantilla aprobada en Meta, y envío de prueba.
 */
import { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import WhatsAppTabs from "../components/WhatsAppTabs";   // v101.192.0
import api from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, RefreshCw, Send, Save, MessageSquare } from "lucide-react";

const OUT = "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)";
const IN_SM = "inset -2px -2px 5px rgba(255,255,255,.95), inset 2px 2px 5px rgba(163,177,198,.55)";
const card = { padding: 22, borderRadius: 24, background: "#ECF0F5", boxShadow: OUT, display: "flex", flexDirection: "column", gap: 14 };
const input = { border: 0, padding: "9px 11px", borderRadius: 11, background: "#E4EAF0", boxShadow: IN_SM, font: "500 13px Inter", color: "#1E293B" };
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);
const LABEL = {
  a2_proposal: "Propuesta de cita al cliente", a2_confirmed: "Cita confirmada al cliente",
  a2_technician_notice: "Aviso al técnico", a4_delay: "Aviso de retraso al cliente",
  fibi_saludo: "Saludo de Fibi al cliente",   // v101.196.0
};

function Flag({ ok, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 12, background: "#E4EAF0", boxShadow: IN_SM, fontSize: 13 }}>
      {ok ? <CheckCircle2 size={16} color="#16A34A" /> : <XCircle size={16} color="#DC2626" />}
      <span style={{ color: "#1E293B", fontWeight: 600 }}>{label}</span>
    </div>
  );
}

export default function AdminWhatsAppConexion() {
  const [st, setSt] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tel, setTel] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/v1/whatsapp/estado"), api.get("/v1/whatsapp/meta/plantillas")]);
      setSt(a.data); setTpl(b.data);
    } catch (e) { toast.error(errMsg(e, "No se pudo leer el estado de WhatsApp")); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    try {
      await api.put(`/v1/whatsapp/meta/plantillas/${row.key}`, {
        meta_name: row.meta_name, language: row.language, active: row.active,
        params: String(row._params ?? (row.params || []).join(", ")).split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Plantilla asociada");
      load();
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); }
  };
  const test = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/v1/whatsapp/prueba", { telefono: tel });
      toast.success(`Mensaje de prueba enviado a ${data.a}. Debe llegar la plantilla «hello_world».`);
      load();
    } catch (e) { toast.error(errMsg(e, "No se pudo enviar la prueba")); } finally { setSending(false); }
  };
  const num = st?.numero_meta;

  return (
    <Layout>
      <div style={{ background: "#E4EAF0", minHeight: "100%", padding: "24px clamp(16px,3vw,32px) 48px", fontFamily: "Inter, system-ui, sans-serif" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
          <WhatsAppTabs />
          <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: ".14em", color: "#475569" }}>GESTIÓN WHATSAPP</div>
              <h1 style={{ margin: "4px 0", fontSize: 28, fontWeight: 800, color: "#0A3747", display: "flex", alignItems: "center", gap: 10 }}>
                <MessageSquare size={26} /> Conexión con WhatsApp Business
              </h1>
              <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>Envío real por la API oficial de Meta. Los secretos se configuran en el servidor y aquí solo se indica si están.</p>
            </div>
            <button onClick={load} style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0, padding: "10px 14px", borderRadius: 13, background: "#ECF0F5", boxShadow: OUT, font: "600 13px Inter", color: "#0A3747", cursor: "pointer" }}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Actualizar
            </button>
          </header>

          {st && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
              <section style={card}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0A3747" }}>Configuración</div>
                <Flag ok={st.envio_real} label={st.envio_real ? "Envío real ACTIVO (Meta)" : `Modo ${st.proveedor} · no se envía`} />
                <Flag ok={!!st.phone_number_id} label={`ID del número: ${st.phone_number_id || "falta"}`} />
                <Flag ok={!!st.waba_id} label={`Cuenta WhatsApp Business: ${st.waba_id || "falta"}`} />
                <Flag ok={st.token_configurado} label="Token de acceso configurado" />
                <Flag ok={st.app_secret_configurado} label="Clave secreta de la app (webhook)" />
                <Flag ok={st.verify_token_configurado} label="Token de verificación del webhook" />
                <div style={{ fontSize: 12, color: "#475569" }}>URL del webhook para Meta: <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>{window.location.origin}{st.webhook ? st.webhook : ""}</code></div>
              </section>
              <section style={card}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0A3747" }}>El número según Meta</div>
                {!num && <div style={{ fontSize: 13, color: "#475569" }}>Se muestra cuando el token y el ID del número están configurados.</div>}
                {num?.error && <div style={{ fontSize: 13, color: "#B91C1C" }}>Meta respondió: {num.error}</div>}
                {num && !num.error && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10 }}>
                    {[["Número", num.display_phone_number], ["Nombre verificado", num.verified_name], ["Estado del nombre", num.name_status],
                      ["Calidad", num.quality_rating], ["Límite de mensajes", num.messaging_limit_tier], ["Verificación", num.code_verification_status]].map(([l, v]) => (
                      <div key={l} style={{ padding: "10px 12px", borderRadius: 12, background: "#E4EAF0", boxShadow: IN_SM }}>
                        <div style={{ fontSize: 11, color: "#475569" }}>{l}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0A3747" }}>{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input aria-label="Teléfono de prueba" placeholder="Teléfono de prueba (09…)" value={tel} onChange={(e) => setTel(e.target.value)} style={{ ...input, flex: 1, minWidth: 160 }} />
                  <button onClick={test} disabled={sending || !tel.trim()} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: 0, padding: "10px 14px", borderRadius: 12, background: "#0A3747", color: "#FFF", font: "700 13px Inter", cursor: "pointer", opacity: sending || !tel.trim() ? 0.6 : 1 }}>
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} color="#E0E621" />} Enviar prueba
                  </button>
                </div>
              </section>
            </div>
          )}

          {tpl && (
            <section style={card}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0A3747" }}>Plantillas aprobadas en Meta</div>
                <div style={{ fontSize: 12, color: "#475569" }}>Para escribirle a un cliente que no escribió en las últimas 24 h, Meta exige una plantilla aprobada. Cada mensaje de AlfaField usa la plantilla indicada; las variables van en el orden de la lista ({"{{1}}"}, {"{{2}}"}…).</div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: 820, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "200px 220px 70px minmax(0,1fr) 110px 90px", gap: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "#475569" }}>
                    <span>MENSAJE</span><span>PLANTILLA EN META</span><span>IDIOMA</span><span>VARIABLES (EN ORDEN)</span><span>EN META</span><span />
                  </div>
                  {tpl.asociacion.map((row, i) => (
                    <div key={row.key} style={{ display: "grid", gridTemplateColumns: "200px 220px 70px minmax(0,1fr) 110px 90px", gap: 10, alignItems: "center", padding: "8px 10px", borderRadius: 12, background: "#E4EAF0", boxShadow: IN_SM }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1E293B" }}>{LABEL[row.key] || row.key}</span>
                      <input aria-label="Plantilla en Meta" value={row.meta_name} onChange={(e) => { const a = [...tpl.asociacion]; a[i] = { ...row, meta_name: e.target.value }; setTpl({ ...tpl, asociacion: a }); }} style={input} />
                      <input aria-label="Idioma" value={row.language} onChange={(e) => { const a = [...tpl.asociacion]; a[i] = { ...row, language: e.target.value }; setTpl({ ...tpl, asociacion: a }); }} style={input} />
                      <input aria-label="Variables" value={row._params ?? (row.params || []).join(", ")} onChange={(e) => { const a = [...tpl.asociacion]; a[i] = { ...row, _params: e.target.value }; setTpl({ ...tpl, asociacion: a }); }} style={input} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: row.aprobada_en_meta ? "#15803D" : row.aprobada_en_meta === false ? "#B91C1C" : "#64748B" }}>
                        {row.aprobada_en_meta ? "Aprobada" : row.aprobada_en_meta === false ? "No aprobada" : "—"}
                      </span>
                      <button onClick={() => save(row)} style={{ display: "inline-flex", alignItems: "center", gap: 5, border: 0, padding: "8px 10px", borderRadius: 10, background: "#ECF0F5", boxShadow: OUT, font: "600 12px Inter", color: "#0A3747", cursor: "pointer" }}>
                        <Save size={13} /> Guardar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              {(tpl.meta || []).length > 0 && (
                <div style={{ fontSize: 12, color: "#475569" }}>En la cuenta de Meta: {tpl.meta.map((m) => `${m.name} (${m.language} · ${m.status})`).join(" · ")}</div>
              )}
            </section>
          )}

          {st?.ultimos?.length > 0 && (
            <section style={card}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#0A3747" }}>Últimos mensajes</div>
              {st.ultimos.map((m, i) => (
                <div key={i} style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, padding: "8px 10px", borderRadius: 10, background: "#E4EAF0", boxShadow: IN_SM }}>
                  <span style={{ fontWeight: 700, color: m.direction === "in" ? "#0284C7" : "#0A3747" }}>{m.direction === "in" ? "Recibido" : "Enviado"}</span>
                  <span>{m.template_key || "—"}</span><span>{m.status}</span><span>{m.modo || ""}</span>
                  {m.error && <span style={{ color: "#B91C1C" }}>{m.error}</span>}
                  <span style={{ marginLeft: "auto", fontFamily: "'JetBrains Mono', monospace", color: "#64748B" }}>{m.fecha?.slice(0, 16)}</span>
                </div>
              ))}
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
}
